import React, { useState } from "react";
import AdminPageShell from "../../components/AdminPageShell";

const cx = (...arr) => arr.filter(Boolean).join(" ");

const Card = ({ children, className }) => (
  <div className={cx("bg-white border border-slate-200 rounded-2xl", className)}>{children}</div>
);

const FieldBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const SelectBase =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30";

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors",
      className
    )}
  />
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      "inline-flex items-center gap-2 rounded-2xl px-5 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors",
      className
    )}
  />
);

export default function ShippingZones() {
  const [shippingData, setShippingData] = useState({
    countries: [],
    deliveryTime: "",
    priceOverride: "",
    courierServices: [],
  });

  const [selectedCountry, setSelectedCountry] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("");
  const [priceOverride, setPriceOverride] = useState("");

  const handleAddCountry = () => {
    if (selectedCountry && deliveryTime && priceOverride) {
      const newCountry = {
        id: Date.now(),
        name: selectedCountry,
        deliveryTime,
        priceOverride: parseFloat(priceOverride),
      };
      setShippingData(prev => ({
        ...prev,
        countries: [...prev.countries, newCountry]
      }));
      setSelectedCountry("");
      setDeliveryTime("");
      setPriceOverride("");
    }
  };

  const handleRemoveCountry = (id) => {
    setShippingData(prev => ({
      ...prev,
      countries: prev.countries.filter(country => country.id !== id)
    }));
  };

  const handleDefineShippingCountries = () => {
    console.log("Defining shipping countries");
    // Handle shipping countries definition
  };

  const handleConnectCouriers = () => {
    console.log("Connecting regional couriers");
    // Handle courier connection
  };

  return (
    <AdminPageShell
      title="Shipping Zones & Options"
      description="Configure shipping zones, delivery times, and courier services."
    >

      {/* Define Shipping Countries Section */}
      <Card className="p-5 md:p-6 mb-6">
        <div className="flex items-center gap-2">
          <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
          <h3 className="text-sm md:text-base font-black tracking-tight text-slate-900">Define Shipping Countries</h3>
        </div>
        <div className="mt-4">
          <SecondaryButton type="button" onClick={handleDefineShippingCountries}>
            Define Shipping Countries
          </SecondaryButton>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
              Select Country
            </label>
            <select 
              value={selectedCountry}
              onChange={(e) => setSelectedCountry(e.target.value)}
              className={SelectBase}
            >
              <option value="">Choose Country</option>
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="GB">United Kingdom</option>
              <option value="DE">Germany</option>
              <option value="FR">France</option>
              <option value="AU">Australia</option>
              <option value="JP">Japan</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
              Estimated Delivery Time
            </label>
            <input
              type="text"
              placeholder="e.g., 5-7 business days"
              className={FieldBase}
              value={deliveryTime}
              onChange={(e) => setDeliveryTime(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
              Price Override (USD)
            </label>
            <input
              type="number"
              placeholder="Override shipping price"
              className={FieldBase}
              value={priceOverride}
              onChange={(e) => setPriceOverride(e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
        </div>
        
        <PrimaryButton type="button" onClick={handleAddCountry}>
          Add Country
        </PrimaryButton>
      </Card>

      {/* Configured Countries List */}
      {shippingData.countries.length > 0 && (
        <Card className="overflow-hidden mb-6">
          <div className="p-5 md:p-6 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
              <h3 className="text-sm md:text-base font-black tracking-tight text-slate-900">Configured Shipping Countries</h3>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">
                    Country
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">
                    Delivery Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">
                    Price Override
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {shippingData.countries.map((country) => (
                  <tr key={country.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                      {country.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      {country.deliveryTime}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                      ${country.priceOverride}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        type="button"
                        onClick={() => handleRemoveCountry(country.id)}
                        className="inline-flex items-center rounded-2xl border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-800 hover:bg-rose-100 transition-colors"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Regional Couriers Section */}
      <Card className="p-5 md:p-6 mb-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="h-4 w-[3px] rounded-full bg-[#D2042D]" aria-hidden="true" />
            <h3 className="text-sm md:text-base font-black tracking-tight text-slate-900">Connect Regional Couriers</h3>
          </div>
          <PrimaryButton type="button" onClick={handleConnectCouriers}>
            Connect Regional Couriers
          </PrimaryButton>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-3">
              Available Courier Services
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input type="checkbox" className="mr-3 h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30" />
                <span className="text-sm text-slate-700">DHL Express</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="mr-3 h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30" />
                <span className="text-sm text-slate-700">FedEx</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="mr-3 h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30" />
                <span className="text-sm text-slate-700">UPS</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="mr-3 h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30" />
                <span className="text-sm text-slate-700">USPS</span>
              </label>
              <label className="flex items-center">
                <input type="checkbox" className="mr-3 h-4 w-4 rounded border-slate-300 text-[#D2042D] focus:ring-[#D2042D]/30" />
                <span className="text-sm text-slate-700">Local Courier Services</span>
              </label>
            </div>
          </div>
          <div>
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-3">
              Courier Integration Status
            </label>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 rounded-2xl border border-emerald-200 bg-emerald-50">
                <span className="text-sm font-semibold text-slate-900">DHL Express</span>
                <span className="inline-flex items-center rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-xs font-bold text-emerald-800">Connected</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-2xl border border-amber-200 bg-amber-50">
                <span className="text-sm font-semibold text-slate-900">FedEx</span>
                <span className="inline-flex items-center rounded-full border border-amber-200 bg-white px-2.5 py-1 text-xs font-bold text-amber-800">Pending</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-2xl border border-slate-200 bg-slate-50">
                <span className="text-sm font-semibold text-slate-900">UPS</span>
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-bold text-slate-800">Not Connected</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Shipping Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-5">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Configured Countries</p>
          <p className="mt-2 text-3xl font-black tracking-tight text-slate-900">{shippingData.countries.length}</p>
        </Card>
        <Card className="p-5">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Active Couriers</p>
          <p className="mt-2 text-3xl font-black tracking-tight text-slate-900">2</p>
        </Card>
        <Card className="p-5">
          <p className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">Delivery Success Rate</p>
          <p className="mt-2 text-3xl font-black tracking-tight text-slate-900">98.5%</p>
        </Card>
      </div>
    </AdminPageShell>
  );
}
